import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Chip,
  Divider,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

interface LegalDisclosurePreviewProps {
  draft: API.LegalDisclosureDraft;
}

const ExternalLink = ({ url }: { url?: string }) => {
  if (!url) {
    return null;
  }
  return (
    <Link href={url} target="_blank" rel="noopener noreferrer">
      核验 <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />
    </Link>
  );
};

export const LegalDisclosurePreview = ({
  draft,
}: LegalDisclosurePreviewProps) => {
  const locales = useMemo(
    () => draft.statements.map((statement) => statement.locale).filter(Boolean),
    [draft.statements]
  );
  const [locale, setLocale] = useState("en_US");

  useEffect(() => {
    if (!locales.includes(locale)) {
      setLocale(locales.includes("en_US") ? "en_US" : locales[0] || "");
    }
  }, [locale, locales]);

  const statement =
    draft.statements.find((item) => item.locale === locale)?.value || "";

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">前台纯文本预览</Typography>
          <Typography variant="caption" color="text.secondary">
            预览不会解析 HTML；实际展示以发布后的接口返回为准。
          </Typography>
        </Box>
        <Chip
          color={draft.enabled ? "success" : "default"}
          label={draft.enabled ? "启用" : "关闭"}
          size="small"
        />
      </Stack>

      <TextField
        select
        size="small"
        label="预览语言"
        value={locale}
        onChange={(event) => setLocale(event.target.value)}
        sx={{ minWidth: 180, mb: 2 }}
      >
        {locales.map((item) => (
          <MenuItem value={item} key={item}>
            {item}
          </MenuItem>
        ))}
      </TextField>

      <Typography sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
        {statement || "尚未填写该语言的声明内容"}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Stack spacing={0.75}>
        <Typography variant="body2">
          <strong>运营主体：</strong>
          {draft.operatorName || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>注册司法辖区：</strong>
          {draft.jurisdiction || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>注册地址：</strong>
          {draft.registeredAddress || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>{draft.companyRegistration.label || "公司登记编号"}：</strong>
          {draft.companyRegistration.number || "—"}{" "}
          <ExternalLink url={draft.companyRegistration.verificationUrl} />
        </Typography>
        {draft.regulatoryLicenses.map((license, index) => (
          <Typography variant="body2" key={`${license.number}-${index}`}>
            <strong>{license.label || `监管牌照 ${index + 1}`}：</strong>
            {license.number || "—"}（{license.authority || "—"}，
            {license.jurisdiction || "—"}）{" "}
            <ExternalLink url={license.verificationUrl} />
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
};
